import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SettingsSidebar } from "@/components/settings/SettingsSidebar";
import { PersonalSettings } from "@/components/settings/PersonalSettings";
import { WorkspaceSettings } from "@/components/settings/WorkspaceSettings";
import { NotificationSettings } from "@/components/settings/NotificationSettings";
import { MemberSettings } from "@/components/settings/MemberSettings";
import { SenderSettings } from "@/components/settings/SenderSettings";
import { ApiKeySettings } from "@/components/settings/ApiKeySettings";
import { ApiActivitySettings } from "@/components/settings/ApiActivitySettings";
import { PlanSettings } from "@/components/settings/PlanSettings";
import { ContactOptionsSettings } from "@/components/settings/ContactOptionsSettings";
import { CustomFieldsSettings } from "@/components/settings/CustomFieldsSettings";
import { SkipOptionsSettings } from "@/components/settings/SkipOptionsSettings";
import { CalendarSettings } from "@/components/settings/CalendarSettings";
import { ImportHistory } from '@/components/settings/ImportHistory';
import { DeletedContactsSettings } from '@/components/settings/DeletedContactsSettings';
import { CallSettings } from '@/components/settings/CallSettings';

const settingsPages = {
  personal: PersonalSettings,
  notifications: NotificationSettings,
  workspace: WorkspaceSettings,
  members: MemberSettings,
  "contact-options": ContactOptionsSettings,
  "custom-fields": CustomFieldsSettings,
  'skip-options': SkipOptionsSettings,
  calendar: CalendarSettings,
  senders: SenderSettings,
  "api-keys": ApiKeySettings,
  "api-activity": ApiActivitySettings,
  plans: PlanSettings,
  'import-history': ImportHistory,
  'deleted-contacts': DeletedContactsSettings,
  'call-settings': CallSettings,
};

type SettingsPageKey = keyof typeof settingsPages;

function isValidPage(page: string): page is SettingsPageKey {
  return page in settingsPages;
}

export function SettingsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const urlPage = params['*'] || '';
  const [activePage, setActivePage] = useState<SettingsPageKey>(
    isValidPage(urlPage) ? urlPage : 'personal'
  );

  // Sync active page when URL changes (e.g. from navigate('/settings/senders'))
  useEffect(() => {
    if (isValidPage(urlPage) && urlPage !== activePage) {
      setActivePage(urlPage);
    }
  }, [urlPage]);

  // Update URL when sidebar selection changes
  const handlePageChange = (page: SettingsPageKey) => {
    setActivePage(page);
    navigate(`/settings/${page}`, { replace: true });
  };

  const ActiveComponent = settingsPages[activePage];

  return (
    <div className="flex h-full">
      {/* Left Sidebar */}
      <div className="w-64 border-r min-h-full">
        <div className="p-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => history.back()}
            className="-ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <SettingsSidebar
          activePage={activePage}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-8">
          <ActiveComponent />
        </div>
      </div>
    </div>
  );
}